import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, TextField, Box, Typography,
  CircularProgress, Alert, Divider, Tooltip
} from '@mui/material'

import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'

import { useState } from 'react'
import Swal from 'sweetalert2'
import Editor from 'components/editor'
import { uploadImageToGhl, useAppServices } from 'hook/services'


export default function EditBlogModal({ blog, handleRefresh }) {

  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [editorKey, setEditorKey] = useState(0)
  const AppService = useAppServices()

  const [form, setForm] = useState({
    title: blog.title || '',
    slug: blog.slug || '',
    metaDescription: blog.metaDescription || '',
    featuredImageAlt: blog.featuredImageAlt || '',
    canonicalUrl: blog.canonicalUrl || '',
    body: blog.body || '',
    author: blog.author || 'Aloca Team',
    featuredImage: blog?.featuredImage || ''
  })

  const handleOpen = () => {
    setForm({
      title: blog.title || '',
      slug: blog.slug || '',
      metaDescription: blog.metaDescription || '',
      featuredImageAlt: blog.featuredImageAlt || '',
      canonicalUrl: blog.canonicalUrl || '',
      body: blog.body || '',
      author: blog.author || 'Aloca Team',
      featuredImage: blog?.featuredImage || ''

    })

    setEditorKey(prev => prev + 1)
    setError('')
    setOpen(true)
  }

  const handleChange = (field) => (e) => {
    setForm(prev => ({
      ...prev,
      [field]: e.target.value
    }))
  }

  const handleEditorChange = (data) => {
    setForm(prev => ({
      ...prev,
      body: data
    }))
  }

  const handleImageUpload = async (e) => {
    const files = e.target.files;

    if (!files || files.length === 0) return;

    const file = files[0];
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await uploadImageToGhl(formData);

      setForm(prev => ({
        ...prev,
        featuredImage: response.imageUrl || response.data
      }));

    } catch (err) {
      console.error("Upload error:", err);
      Swal.fire('Upload Failed', 'Image upload failed', 'error');
    }
  }

  const handleSave = async () => {

    if (!form.title || !form.body) {
      setError('Title and Body are required.')
      return
    }

    setSaving(true)
    setError('')

    try {
      const payload = {
        id: blog._id,
        ...form
      }
      await AppService.aicollection.update_status({
        payload
      })

      setOpen(false)

      handleRefresh()

      Swal.fire({
        icon: 'success',
        title: 'Collection Page Updated!',
        timer: 2000,
        showConfirmButton: false
      })

    } catch (err) {

      setError('Failed to update blog.')

      Swal.fire(
        'Error',
        'Failed to update blog',
        'error'
      )

    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Tooltip title="Edit">
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            color: '#f57c00',
            '&:hover': { backgroundColor: '#fff3e0' }
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '16px' }
        }}
      >

        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2, px: 3
          }}
        >

          <Typography variant="h6" fontWeight={700} style={{ color: "white" }} fontSize="1rem">
            Edit Collection Page
          </Typography>

          <IconButton
            onClick={() => setOpen(false)}
            sx={{ color: 'white' }}
            size="small"
          >
            <CloseIcon color="white" />
          </IconButton>

        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 3 }}>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          <Box display="flex" flexDirection="column" gap={2} mt={8}>

            <TextField
              label="Title (≤60 chars)"
              fullWidth
              value={form.title}
              onChange={handleChange('title')}
              inputProps={{ maxLength: 60 }}
              helperText={`${form.title.length}/60`}
            />

            <TextField
              label="Slug"
              fullWidth
              value={form.slug}
              onChange={handleChange('slug')}
              helperText="lowercase-hyphenated"
            />

            <TextField
              label="Meta Description (≤150 chars)"
              fullWidth
              value={form.metaDescription}
              onChange={handleChange('metaDescription')}
              inputProps={{ maxLength: 150 }}
              helperText={`${form.metaDescription.length}/150`}
            />

            <Box display="flex" gap={2}>

              <TextField
                label="Featured Image Alt"
                fullWidth
                value={form.featuredImageAlt}
                onChange={handleChange('featuredImageAlt')}
              />


              <TextField
                label="Author"
                fullWidth
                value={form.author}
                onChange={handleChange('author')}
              />

            </Box>

            <TextField
              label="Canonical URL"
              fullWidth
              value={form.canonicalUrl}
              onChange={handleChange('canonicalUrl')}
            />
            <Box>

              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ letterSpacing: 1 }}
              >
                Collection  CARD IMAGE
              </Typography>

              <Box mt={1} display="flex" gap={2} alignItems="center">

                <Button
                  variant="outlined"
                  component="label"
                  sx={{ textTransform: 'none', color: "#1A73E8" }}
                >
                  Upload Image
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>

                {form.featuredImage && (
                  <Box
                    component="img"
                    src={form.featuredImage}
                    alt="Preview"
                    sx={{
                      width: 120,
                      height: 80,
                      objectFit: 'cover',
                      borderRadius: 2,
                      border: '1px solid #ddd'
                    }}
                  />
                )}

              </Box>

            </Box>

            <Divider />

            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ letterSpacing: 1 }}
            >
              Collection Page BODY
            </Typography>


            <Box
              sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
                '& .ck-editor__editable': {
                  minHeight: 380,
                  fontSize: '0.95rem',
                  px: 2
                }
              }}
            >

              <Editor
                key={editorKey}
                data={form.body}
                handleChange={handleEditorChange}
              />

            </Box>


          </Box>

        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 3, gap: 1 }}>

          <Button
            onClick={() => setOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: '8px', textTransform: 'none', background: 'linear-gradient(135deg, #1A73E8, #0D47A1)' }}

            style={{
              color: "white"
            }}

          >

            {saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              'Save Changes'
            )}

          </Button>

        </DialogActions>

      </Dialog>
    </>
  )
}